import { LegTimeStampStatusEnum } from '@enums/leg-timestamp-status.enum';
import {
  AirTimeStampInterface,
  GroundTimeStampInterface,
  LegTimeStampInterface,
  LegTimeStampRowInterface,
} from '@interfaces/leg-timestamp.interface';

type LegTimeStampEntry = readonly [LegTimeStampStatusEnum, Date | null];

/**
 * Builds the rows of the Leg Time Stamp table.
 * The leg type is derived from the response itself: the API returns either
 * `airTimeStamp` or `groundTimeStamp`, never both, so Air and Ground
 * timestamps can never end up mixed in the same table.
 */
export function mapLegTimeStampRows(response: LegTimeStampInterface | null): LegTimeStampRowInterface[] {
  if (response?.airTimeStamp) {
    return mapAirTimeStampRows(response.airTimeStamp);
  }

  if (response?.groundTimeStamp) {
    return mapGroundTimeStampRows(response.groundTimeStamp);
  }

  return [];
}

/** Total leg duration in hours, whichever timestamp object the response carries. */
export function getLegTimeStampDuration(response: LegTimeStampInterface | null): number {
  return response?.airTimeStamp?.duration ?? response?.groundTimeStamp?.duration ?? 0;
}

function mapAirTimeStampRows(airTimeStamp: AirTimeStampInterface): LegTimeStampRowInterface[] {
  return buildRows([
    [LegTimeStampStatusEnum.OnStandby, airTimeStamp.onStandBy],
    [LegTimeStampStatusEnum.Repositioning, airTimeStamp.repositioning],
    [LegTimeStampStatusEnum.InPosition, airTimeStamp.inPosition],
    [LegTimeStampStatusEnum.WheelsUp, airTimeStamp.wheelsUp],
    [LegTimeStampStatusEnum.Landed, airTimeStamp.landed],
    [LegTimeStampStatusEnum.InboundWheelsUp, airTimeStamp.inboundWheelsUp],
    [LegTimeStampStatusEnum.InboundLanded, airTimeStamp.inboundLanded],
    [LegTimeStampStatusEnum.JobDone, airTimeStamp.jobDone],
  ]);
}

function mapGroundTimeStampRows(groundTimeStamp: GroundTimeStampInterface): LegTimeStampRowInterface[] {
  return buildRows([
    [LegTimeStampStatusEnum.EnRoute, groundTimeStamp.enroutetime],
    [LegTimeStampStatusEnum.OnScene, groundTimeStamp.onSceneTime],
    [LegTimeStampStatusEnum.PickedUp, groundTimeStamp.pickupTime],
    [LegTimeStampStatusEnum.DroppedOff, groundTimeStamp.dropOffTime],
    [LegTimeStampStatusEnum.DriverDone, groundTimeStamp.driverDoneTime],
    [LegTimeStampStatusEnum.JobDone, groundTimeStamp.jobDoneTime],
  ]);
}

/** Keeps only the timestamps the backend actually returned, in display order. */
function buildRows(entries: readonly LegTimeStampEntry[]): LegTimeStampRowInterface[] {
  return entries.flatMap(([status, time]) => (time == null ? [] : [{ Status: status, Time: time, hide: true }]));
}
